/**
 * AppErrorBoundary — top-level React error boundary (Pillar 3 reliability).
 * Prevents a full white screen on uncaught render errors; reports to Sentry in production.
 */

import React, { Component } from 'react';
import { Box, Typography, Alert, Button } from '@mui/material';
import * as Sentry from '@sentry/browser';

class AppErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[AppErrorBoundary] Uncaught error:', error);
    console.error('[AppErrorBoundary] Component stack:', errorInfo?.componentStack);
    if (process.env.NODE_ENV === 'production') {
      Sentry.captureException(error, {
        tags: { feature: 'app-shell' },
        extra: { componentStack: errorInfo?.componentStack },
      });
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.assign('/');
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
            bgcolor: 'background.default',
          }}
        >
          <Alert
            severity="error"
            sx={{ maxWidth: 480, width: '100%' }}
            action={
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
                <Button color="inherit" size="small" onClick={this.handleReload}>
                  Reload
                </Button>
                <Button color="inherit" size="small" onClick={this.handleGoHome}>
                  Home
                </Button>
              </Box>
            }
          >
            <Typography variant="h6" component="div" gutterBottom>
              Something went wrong
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {this.state.error?.message || 'An unexpected error occurred. Try reloading the page.'}
            </Typography>
          </Alert>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default AppErrorBoundary;
